import React, { useLayoutEffect, useState } from 'react';

import { StudyPlanEntity } from 'Models/Entities';
import cn from 'classnames';

import Wizard from 'Views/Components/Wizard/Wizard';
import ChipGroup from 'Views/Components/Chip/ChipGroup';
import Chip from 'Views/Components/Chip/Chip';
import { useWizardContext } from 'Views/Components/Wizard/WizardContext';
import ProgramDetailsModal from 'Views/Pages/Frontend/StudyPlanWizard/StudyPlan/ProgramDetailsModal';
import {
	IStudyPlanWizardData,
} from 'Views/Pages/Frontend/StudyPlanWizard/StudyPlanWizard';
import {
	Button, Display,
} from 'Views/Components/Button/Button';
import classnames from 'classnames';
import SelectionNoticeModal from 'Views/Pages/Frontend/StudyPlanWizard/SelectionNoticeModal';
import useScreenSize from 'Hooks/useScreenSize';

import {
	RequirementNode,
	checkRequirementRulesViolation,
} from 'Views/Pages/Frontend/StudyPlanWizard/RequirementTreeGeneration';
import { Conditional } from 'Views/Components/Conditional/Conditional';
import { useRequirementIndex } from 'Views/Pages/Frontend/StudyPlanWizard/RequirementIndexContext';
import InfoIconButton from 'Views/Components/InfoIconButton/InfoIconButton';
import {
	useCourseDuplicates,
} from 'Views/Pages/Frontend/StudyPlanWizard/RequirementFulfilmentCheck/useCourseDuplicates';

import {
	sumBy, uniqBy, capitalize, toLower,
} from 'lodash';
import useUnitSelected from 'Hooks/useUnitSelected';
import _ from 'lodash';

interface IStudyPlanWizardSidebarProps {
	studyPlan?: StudyPlanEntity;
	currentComponentId?: string;
	onComponentClick?: (componentId: string) => void;
	showProgramDetails?: boolean;
}

const getPlanChips = (studyPlan: StudyPlanEntity) => {
	const { programVersion, campus, programStructure } = studyPlan;

	const chips: Array<{ key: string; label: string }> = [];

	if (programVersion) {
		chips.push(
			{
				key: 'program-code',
				label: programVersion?.program.code || '',
			},
			{
				key: 'plan-year',
				label: programVersion?.getDisplayName() || '',
			},
		);
	}

	if (campus) {
		chips.push(
			{
				key: 'program-campus',
				label: campus?.getDisplayName() || '',
			},
		);
	}

	if (programStructure) {
		chips.push(
			{
				key: 'program-structure',
				label: programStructure?.getDisplayName() || '',
			},
		);
	}

	return chips;
};

const StudyPlanWizardSidebar = ({
	studyPlan,
	currentComponentId,
	onComponentClick,
	showProgramDetails = false,
}: IStudyPlanWizardSidebarProps) => {
	const { data } = useWizardContext<IStudyPlanWizardData>();

	const isMobile = useScreenSize();

	const [showMoreProgramDetails, setShowMoreProgramDetails] = useState(false);
	const [showProgramDetailsModal, setShowProgramDetailsModal] = useState(false);
	const [showSelectionNoticeModal, setShowSelectionNoticeModal] = useState(false);

	const {
		planSelections,
		requirementsById,
		coursesById,
		duplicateCourseIds,
		requirementTree,
	} = useRequirementIndex();

	const duplicateCourses = useCourseDuplicates(
		planSelections,
		duplicateCourseIds,
		requirementsById,
		coursesById,
		!showProgramDetails,
	);

	const {
		displayingRequirements,
		totalUnitsSelected,
		duplicateUnitsSelected,
		isProgramTotalConditionNotMet,
		requiredTotalUnitCondition,
	} = useUnitSelected({
		requirementTree: requirementTree,
		studyPlan: data?.studyPlan,
		coursesById,
		duplicateCourses,
	});

	const programVersion = data?.studyPlan?.programVersion;

	const chipConfigList: Array<{ key: string; label: string }> = data?.studyPlan
		? getPlanChips(data.studyPlan)
		: [];

	const getUnitLabel = ({ unitCount, unitMinimum }: RequirementNode) => {
		const unitTotalSuffix = unitMinimum === undefined
			? ''
			: `/${unitMinimum}`;

		return `${unitCount}${unitTotalSuffix}`;
	};

	useLayoutEffect(() => {
		if (isMobile) {
			setShowMoreProgramDetails(false);
		} else {
			setShowMoreProgramDetails(true);
		}
	}, [isMobile]);

	/**
	 * Check if a requirement node has at least 1 invalid child node
	 * Invalid child nodes are nodes that do not satisfy their
	 * requirement rules
	 *
	 * @param requirementNode - the requirement node to check
	 */
	const hasInvalidChildNode = (requirementNode: RequirementNode): boolean => {
		return requirementNode.childNodes.some(childNode => {
			return checkRequirementRulesViolation(childNode) || hasInvalidChildNode(childNode);
		});
	};

	const showInfoIcon = (requirementNode: RequirementNode) => {
		return checkRequirementRulesViolation(requirementNode) || hasInvalidChildNode(requirementNode);
	};

	return (
		<Wizard.SideBar>
			<aside className="top program-info" aria-labelledby="program-name-sidebar">
				<div className={classnames(
					'program-info__content',
					showMoreProgramDetails ? 'expanded' : 'collapsed',
				)}
				>
					<div>
						<h3 className="program-name" id="program-name-sidebar">
							{studyPlan?.programVersion.program.displayName}
						</h3>
						<ChipGroup>
							{chipConfigList.map(chip => (<Chip key={chip.key} chipLabel={chip.label} />))}
						</ChipGroup>
						<ChipGroup className="program-detail-chip">
							<Button
								className="button-chip"
								onClick={() => setShowProgramDetailsModal(true)}
							>
								{`View ${toLower(window.lexicon.program)} details`}
							</Button>
						</ChipGroup>
					</div>
					<Conditional
						condition={isMobile}
						component={() => (
							<Button
								className={'collapse-btn'}
								display={Display.Text}
								icon={{
									icon: !showMoreProgramDetails ? 'expand-more' : 'remove',
									iconPos: 'icon-right',
								}}
								onClick={() => setShowMoreProgramDetails(!showMoreProgramDetails)}
							/>
						)}
						componentProps={{ key: 'mobile-expand-btn' }}
					/>
				</div>
			</aside>
			{
				showProgramDetails && requirementTree
					? (
						<div className="bottom" role="navigation" aria-labelledby="units_display_nav">
							<div className="menu-list">
								<p id="units_display_nav">
									{capitalize(window.lexicon.units)} selected
								</p>
								{
									displayingRequirements
										.map(({ requirementNode, requirement }) => {
											const isActive = currentComponentId === requirementNode.requirement.id;
											const component = requirementNode.requirement;

											return (
												<button
													key={component.id}
													className={cn(
														'menu-list-item',
														'menu-list-button',
														{ active: isActive },
													)}
													onClick={e => {
														onComponentClick?.(component.id);
														(e.target as HTMLButtonElement).blur();
													}}
													aria-labelledby={_.snakeCase(requirement.displayName)}
												>
													<span
														className={cn('menu-list-display', { active: isActive })}
														id={_.snakeCase(requirement.displayName)}
													>
														{requirement.displayName}
													</span>
													<div className="menu-list-info">
														<span>
															{getUnitLabel(requirementNode)}
														</span>
														{showInfoIcon(requirementNode) && (
															<InfoIconButton
																ariaLabel={`${requirement.displayName} requirement`}
																hasError={showInfoIcon(requirementNode)}
																onClick={e => {
																	e.preventDefault();
																	e.stopPropagation();
																	setShowSelectionNoticeModal(true);
																}}
																id={requirement.id}
															/>
														)}
													</div>
												</button>
											);
										})
								}
								<div
									key="program-total"
									className="menu-list-item program-total"
								>
									<span className="menu-list-display">
										{capitalize(window.lexicon.program)} total
									</span>
									<div>
										<span>
											{totalUnitsSelected}
											{
												requiredTotalUnitCondition !== undefined
													? `/${requiredTotalUnitCondition?.units}`
													: null
											}
										</span>
										{isProgramTotalConditionNotMet && (
											<InfoIconButton
												hasError
												ariaLabel="program total"
												onClick={e => {
													e.preventDefault();
													e.stopPropagation();
													setShowSelectionNoticeModal(true);
												}}
												id="program-total-info-icon"
											/>
										)}
									</div>
								</div>
								<div
									key="duplicate-total"
									className="menu-list-item"
								>
									<span className="menu-list-label">
										{capitalize(window.lexicon.duplicates)}
									</span>
									<span>
										{duplicateUnitsSelected}
									</span>
								</div>
							</div>
						</div>
					)
					: null
			}
			{
				programVersion ? (
					<ProgramDetailsModal
						isOpen={showProgramDetailsModal}
						programVersion={programVersion}
						onCloseModal={() => setShowProgramDetailsModal(false)}
					/>
				) : null
			}
			<SelectionNoticeModal
				isOpen={showSelectionNoticeModal}
				onCloseModal={() => setShowSelectionNoticeModal(false)}
			/>
		</Wizard.SideBar>
	);
};

export default StudyPlanWizardSidebar;
